<template>
    <div class="ui-media-gallery">
        <div class="ui-media-gallery__list">
            <div
                v-for="(media, index) in mediaList"
                :key="media.id"
                class="ui-media-gallery__item"
            >
                <div class="ui-media-gallery__content" @click="onMediaItemClick(index)">
                    <div v-if="media.previewUrl" class="ui-media-gallery__image-wrapper">
                        <img
                            :style="{ transform: `rotate(${media.rotation}deg)` }"
                            :src="media.previewUrl || noPhotoImage"
                            class="ui-media-gallery__image"
                            @error="onImageError"
                        />
                    </div>
                    <div v-else class="ui-media-gallery__image-wrapper no-image">
                        <img
                            v-svg-inline
                            class="ui-media-gallery__image"
                            src="@/assets/icons/ui/camera.svg"
                        />
                    </div>

                    <div v-if="media.previewUrl" class="ui-media-gallery__actions">
                        <div class="ui-media-gallery__action-button rotate" @click.stop="rotateMedia(index)">
                            <img v-svg-inline src="@/assets/icons/ui/rotate.svg" />
                        </div>

                        <div class="ui-media-gallery__action-button delete" @click.stop="removeMedia(index)">
                            <img v-svg-inline src="@/assets/icons/ui/close.svg" />
                        </div>
                    </div>
                </div>

                <div class="ui-media-gallery__label">
                    {{ media.label }}
                </div>
            </div>
        </div>

        <div
            v-if="mediaList.length < CarMaxPhotos"
            class="ui-media-gallery__add"
            @click="addAdditionalMedia"
        >
            <div class="ui-media-gallery__add-button">
                <img
                    v-svg-inline
                    class="ui-media-gallery__add-image"
                    src="@/assets/icons/ui/camera.svg"
                />
                <span>Добавить ещё фото</span>
            </div>
        </div>

        <input
            ref="fileInput"
            type="file"
            :accept="computedAccept"
            class="ui-media-gallery__file-input"
            hidden
            @change="handleFileInput"
        />
    </div>
</template>

<script lang="ts" setup>
    import noPhotoImage from '@/assets/images/cars/no-photo.webp';

    import { computed, PropType, ref, watch } from 'vue';

    import { MediaItem } from '@/types/form.type.ts';
    import { CarPhotoType, CarMaxPhotos } from '@/types/car.type.ts';

    import useNotifications from '@/utils/useNotifications.ts';

    const props = defineProps({
        initialMedia: {
            type: Array as () => MediaItem[],
            required: true,
            default: () => [],
        },
        additionalType: {
            type: String,
            default: CarPhotoType.Additional,
        },
        additionalLabel: {
            type: String,
            default: 'Дополнительно',
        },
        allowedFileTypes: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
    });

    const emit = defineEmits(['media-updated']);

    const { showError } = useNotifications();

    //  Локальный state со списком медиа
    const mediaList = ref<MediaItem[]>([]);

    const selectedIndex = ref<number | null>(null);
    const fileInput = ref<HTMLInputElement | null>(null);

    const localAllowedFileTypes = props.allowedFileTypes.flatMap(allowedFileType => {
        if (allowedFileType === 'video/*') {
            return ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska'];
        }
        else if (allowedFileType === 'image/*') {
            return ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        }

        return allowedFileType;
    });

    const computedAccept = computed(() => (localAllowedFileTypes.length ? localAllowedFileTypes.join(',') : ''));

    watch(
        () => props.initialMedia,
        () => fillInitialMedia(),
        { deep: true },
    );

    // Хэндлер клика на конкретное окно
    const onMediaItemClick = (index: number): void => {
        selectedIndex.value = index;
        triggerFileInput();
    };

    const triggerFileInput = (): void => {
        fileInput.value?.click();
    };

    const handleFileInput = (event: Event): void => {
        const files = (event.target as HTMLInputElement).files;
        if (!files || files.length === 0) {
            return;
        }

        if (selectedIndex.value === null) {
            return;
        }

        const file = files[0];
        const objectUrl = URL.createObjectURL(file);

        const acceptedFile = localAllowedFileTypes.length !== 0 && localAllowedFileTypes.includes(file.type);
        if (!acceptedFile) {
            showError({ message: 'Не поддерживаемый формат файла' });

            return;
        }

        const media = mediaList.value[selectedIndex.value];
        media.file = file;
        media.previewUrl = objectUrl;

        emit('media-updated', mediaList.value);

        (event.target as HTMLInputElement).value = '';
        selectedIndex.value = null;
    };

    // Можно заменить проверку isRequired, на будущее
    const removeMedia = (index: number): void => {
        const media = mediaList.value[index];

        if (media.type === props.additionalType) {
            mediaList.value.splice(index, 1);
        }
        else {
            media.file = null;
            media.previewUrl = '';
            media.rotation = 0;
        }

        emit('media-updated', mediaList.value);
    };

    const rotateMedia = (index: number): void => {
        const media = mediaList.value[index];
        media.rotation = (media.rotation + 90) % 360;

        emit('media-updated', mediaList.value);
    };

    const addAdditionalMedia = (): void => {
        if (mediaList.value.length >= CarMaxPhotos) {
            return;
        }

        const id = String(Date.now());
        mediaList.value.push({
            id,
            type: props.additionalType,
            label: props.additionalLabel,
            previewUrl: '',
            file: null,
            rotation: 0,
        });

        emit('media-updated', mediaList.value);
    };

    function onImageError(e: Event): void {
        (e.target as HTMLImageElement).src = noPhotoImage;
    }

    function fillInitialMedia(): void {
        mediaList.value = props.initialMedia.map((media) => ({
            ...media,
            rotation: media.rotation || 0,
            previewUrl: media.previewUrl || '',
        }));
    }

    fillInitialMedia();
</script>

<style lang="scss" scoped>
    @use './UIMediaGallery.scss' as *;
</style>
